import React, { useCallback, useRef, useState } from 'react';
import { TDT_FREQS, TDT_MAX_SECONDS } from './specialLogic';

/**
 * TDTGraph — Tone Decay Test graph.
 * X-axis: Time (seconds) 0..60
 * Y-axis: two response bands — Heard (upper) and Not Heard (lower)
 * One dot per test frequency (colour-coded) plotted at (decay_time, band).
 * Click at a time point applies the value to the currently-selected freq.
 */
const TDTGraph = ({
  values,        // { '500': 60, '1000': 60, ... }
  selectedFreq,  // active frequency for graph clicks
  onSelectFreq,
  onSetValue,    // (freq, seconds) => void
  readOnly = false,
  width = 460, height = 200,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const marginL = 42, marginR = 12, marginT = 24, marginB = 34;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const bandH = plotH / 2;
  const heardY = marginT + bandH / 2;
  const notHeardY = marginT + plotH - bandH / 2;

  const xScale = useCallback(
    (sec) => marginL + (Math.max(0, Math.min(TDT_MAX_SECONDS, sec)) / TDT_MAX_SECONDS) * plotW,
    [plotW],
  );
  const xInv = useCallback(
    (px) => {
      const raw = ((px - marginL) / plotW) * TDT_MAX_SECONDS;
      return Math.max(0, Math.min(TDT_MAX_SECONDS, Math.round(raw)));
    },
    [plotW],
  );

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const handleClick = (evt) => {
    if (readOnly || !onSetValue || !selectedFreq) return;
    const p = toViewBox(evt);
    if (!p) return;
    if (p.x < marginL - 6 || p.x > width - marginR + 6) return;
    if (p.y < marginT - 6 || p.y > height - marginB + 6) return;
    onSetValue(selectedFreq, xInv(p.x));
  };

  const handleMove = (evt) => {
    if (readOnly || !selectedFreq) return;
    const p = toViewBox(evt);
    if (!p) { setHover(null); return; }
    if (p.x < marginL || p.x > width - marginR || p.y < marginT || p.y > height - marginB) {
      setHover(null); return;
    }
    setHover({ x: xScale(xInv(p.x)), sec: xInv(p.x) });
  };

  const timeTicks = [0, 10, 20, 30, 40, 50, 60];

  const colorFor = (freq) => {
    // deterministic palette per freq — same order as TDT_FREQS
    const palette = ['#dc2626', '#ea580c', '#16a34a', '#2563eb'];
    return palette[TDT_FREQS.indexOf(freq)] || '#64748b';
  };

  // Build the connecting line across dots in freq order
  const points = TDT_FREQS.map((f) => {
    const raw = values?.[String(f)];
    if (raw === undefined || raw === null || raw === '' || !Number.isFinite(Number(raw))) return null;
    const sec = Number(raw);
    const y = sec >= TDT_MAX_SECONDS ? heardY : notHeardY;
    return { freq: f, sec, x: xScale(sec), y };
  }).filter(Boolean);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <div className="w-full">
      {!readOnly && (
        <div className="flex items-center gap-1 mb-1" data-testid="tdt-freq-chips">
          <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Freq:</span>
          {TDT_FREQS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`tdt-freq-${f}`}
              onClick={() => onSelectFreq && onSelectFreq(f)}
              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${selectedFreq === f ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              style={selectedFreq === f ? { backgroundColor: colorFor(f) } : {}}
            >{f >= 1000 ? `${f / 1000}k` : f}</button>
          ))}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
        data-testid="tdt-graph"
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Bands */}
        <rect x={marginL} y={marginT} width={plotW} height={bandH} fill="#ecfdf5" />
        <rect x={marginL} y={marginT + bandH} width={plotW} height={bandH} fill="#fef2f2" />
        <line x1={marginL} x2={width - marginR} y1={marginT + bandH} y2={marginT + bandH} stroke="#94a3b8" strokeWidth="0.6" strokeDasharray="3 3" />
        {/* Y labels */}
        <text x={marginL - 6} y={heardY + 3} fontSize="10" fill="#065f46" textAnchor="end" fontWeight="600">Heard</text>
        <text x={marginL - 6} y={notHeardY + 3} fontSize="10" fill="#991b1b" textAnchor="end" fontWeight="600">Not Heard</text>
        <text x={marginL - 32} y={marginT + plotH / 2 - 18} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 32}, ${marginT + plotH / 2})`} textAnchor="middle">Response</text>
        {/* X ticks */}
        {timeTicks.map((t) => (
          <g key={t}>
            <line x1={xScale(t)} x2={xScale(t)} y1={marginT} y2={height - marginB} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(t)} y={height - marginB + 12} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">{t}</text>
          </g>
        ))}
        <text x={marginL + plotW / 2} y={height - 4} fontSize="9" fill="#475569" textAnchor="middle">Time (seconds)</text>
        {/* Connecting line */}
        {linePath && <path d={linePath} fill="none" stroke="#dc2626" strokeWidth="1.8" />}
        {/* Points */}
        {points.map((p) => (
          <g key={p.freq}>
            <circle cx={p.x} cy={p.y} r="4" fill={colorFor(p.freq)} />
            <text x={p.x} y={p.y - 8} fontSize="8" fill={colorFor(p.freq)} textAnchor="middle" fontWeight="700">
              {p.freq >= 1000 ? `${p.freq / 1000}k` : p.freq}
            </text>
          </g>
        ))}
        {/* Hover ghost */}
        {!readOnly && hover && selectedFreq && (
          <g style={{ pointerEvents: 'none' }} data-testid="tdt-hover">
            <line x1={hover.x} x2={hover.x} y1={marginT} y2={height - marginB} stroke={colorFor(selectedFreq)} strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
            <rect x={hover.x + 4} y={marginT + 2} width="46" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={hover.x + 6} y={marginT + 12} fontSize="9" fill="#ffffff" fontWeight="600">
              {selectedFreq >= 1000 ? `${selectedFreq / 1000}k` : selectedFreq} · {hover.sec}s
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default TDTGraph;
